/**
 * Canonical warranty product identity.
 * Brand + exact model tokens — not loose substring matching.
 */

export type WarrantyProductIdentity = {
  brandKey: string;
  modelTokens: string[];
  identityKey: string;
  displayBrand: string;
  displayModel: string;
};

const PLUS_BRANDS: Array<{ pattern: RegExp; key: string }> = [
  { pattern: /^t\s*\+?\s*a$/i, key: "t+a" },
  { pattern: /^t\s*and\s*a$/i, key: "t+a" },
];

export function normalizeWarrantyBrandKey(raw: string | null | undefined): string {
  const collapsed = String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/[&]/g, "+")
    .replace(/\s+/g, " ");
  if (!collapsed) return "";
  const compact = collapsed.replace(/\s+/g, "");
  for (const entry of PLUS_BRANDS) {
    if (entry.pattern.test(collapsed) || entry.pattern.test(compact)) return entry.key;
  }
  return collapsed.replace(/[^a-z0-9+]+/g, " ").replace(/\s+/g, " ").trim();
}

/** Split a model into letter / digit tokens so P3100HV and P 3100 HV match, but PA 3100 HV does not. */
export function tokenizeWarrantyModel(raw: string | null | undefined): string[] {
  const text = String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
  if (!text) return [];
  const tokens: string[] = [];
  for (const chunk of text.split(/\s+/)) {
    const parts = chunk.match(/[a-z]+|[0-9]+/g) ?? [];
    for (const part of parts) {
      if (part) tokens.push(part);
    }
  }
  return tokens;
}

export function warrantyProductIdentity(
  brand: string | null | undefined,
  model: string | null | undefined,
): WarrantyProductIdentity {
  const brandKey = normalizeWarrantyBrandKey(brand);
  const modelTokens = tokenizeWarrantyModel(model);
  return {
    brandKey,
    modelTokens,
    identityKey: `${brandKey}|${modelTokens.join("|")}`,
    displayBrand: String(brand ?? "").trim(),
    displayModel: String(model ?? "").trim(),
  };
}

export function warrantyIdentitiesEqual(
  a: { brand?: string | null; model?: string | null },
  b: { brand?: string | null; model?: string | null },
): boolean {
  const left = warrantyProductIdentity(a.brand, a.model);
  const right = warrantyProductIdentity(b.brand, b.model);
  return Boolean(left.brandKey && left.modelTokens.length > 0 && left.identityKey === right.identityKey);
}

export const T_PLUS_A_P3100_HV_IDENTITY = warrantyProductIdentity("T+A", "P 3100 HV").identityKey;
